import React from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Box as Card,
  Box as Container,
  DataListItem,
} from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { Approval } from 'constants/General'
import { usePutConfirmPaymentMutation } from 'hooks/mutations/usePutConfirmPaymentMutation'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useMinutesSecondsTimer } from 'hooks/useMinutesSecondsTimer'
import { contactAtom, contactInstrumentAtom } from 'pages/Contacts/atoms/contactAtoms'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { SendStep } from 'types/walletActions'
import { formatString, roundNumberWithCommas } from 'utils/utils'
import { paymentAtom, sendStepAtom, sendWalletAtom } from './atoms/atoms'

export const SendReview = () => {
  const { t } = useTranslation()
  const contact = useAtomValue(contactAtom)
  const sendWallet = useAtomValue(sendWalletAtom)
  const contactInstrument = useAtomValue(contactInstrumentAtom)
  const payment = useAtomValue(paymentAtom)
  const setSendStep = useSetAtom(sendStepAtom)
  const { mutate: putConfirmPayment, isPending: isConfirmingPayment } =
    usePutConfirmPaymentMutation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { approvalsIsRequiredForFlow } = useApprovalsConfigForFlow(Approval.FLOWS.CRYPTO_PAYOUT)
  const [walletAction] = useWalletActionContext()

  const isPegged = payment?.pegged ?? false
  const peggedAmount = payment?.pegStablecoinDetails?.paidCurrency?.amount ?? 0
  const peggedRate = payment?.pegStablecoinDetails?.exchangeRate?.rate ?? 0

  const handleConfirmPayment = () => {
    resetRequestError()
    putConfirmPayment(payment?.uuid, {
      onSuccess: () => {
        if (approvalsIsRequiredForFlow) {
          setSendStep(SendStep.APPROVAL)
        } else {
          setSendStep(SendStep.COMPLETE)
        }
      },
      onError: (error: BaseErrorResponse) => {
        let errorMessage = ''

        if (error?.errorList?.[0]?.code === 'MER-PAY-2012') {
          errorMessage = t('wallets.send.walletInsufficientFunds')
        }

        const sentryEventId = logSentryErrorAndGetEventId(
          `Failed API request to /v1/pay/${payment?.uuid}/confirm/summary with status ${error?.status} - usePutConfirmPaymentMutation`,
          error,
          {
            data: payment?.uuid,
          }
        )

        setRequestError({
          handledMessage: errorMessage || error?.message,
          errorCode: error?.status,
          sentryTraceId: sentryEventId,
          show: true,
        })
      },
    })
  }

  return (
    <Container width="full" data-testid="review-container">
      {requestError?.show && (
        <Box paddingT={4} paddingB={16}>
          <RequestErrorCallout requestError={requestError} />
        </Box>
      )}
      <Card paddingT={16}>
        <CountdownCallout onExpiry={walletAction?.onClose} />
        <Box paddingT={16} flexCol>
          <Box flexCol gap={4} paddingB={16}>
            <DataListItem
              id="sending-from-wallet"
              testId="sending-from-wallet"
              title={t('wallets.send.sendingFromWallet')}
              body={`${sendWallet?.description} | ${formatString(
                t('balanceWithCurrency'),
                roundNumberWithCommas(sendWallet?.balance),
                sendWallet?.currency?.code
              )}`}
              BodyAvatar={
                <Avatar
                  size="small"
                  type="currency"
                  name={sendWallet?.currency ? sendWallet?.currency?.code?.toLowerCase() : ''}
                />
              }
              layout="horizontal"
              size="large"
            />

            <DataListItem
              id="sending-to"
              testId="sending-to"
              title={t('wallets.send.sendingTo')}
              body={`${contact?.name ? contact?.name : ''} | ${contactInstrument?.address}`}
              layout="horizontal"
              size="large"
            />

            <DataListItem
              id="unique-reference"
              testId="unique-reference"
              title={t('wallets.send.uniqueReference')}
              body={payment?.reference}
              layout="horizontal"
              size="large"
            />

            <DataListItem
              id="beneficiary-will-receive"
              testId="beneficiary-will-receive"
              title={t('wallets.send.beneficiaryWillReceive')}
              body={`${isPegged ? peggedAmount : payment?.paidCurrency?.amount} ${payment?.paidCurrency?.currency}`}
              layout="horizontal"
              size="large"
            />
          </Box>

          <Box divideBox="top" className="border-stroke-static" paddingT={16} />
          <Box flexCol paddingB={16}>
            <DataListItem
              id="amount-to-send"
              testId="amount-to-send"
              title={t('wallets.send.amountToSend')}
              body={`${payment?.walletCurrency?.amount} ${payment?.walletCurrency?.currency}`}
              layout="horizontal"
              size="large"
            />

            <DataListItem
              id="exchange-rate"
              testId="exchange-rate"
              title={t('wallets.send.exchangeRate')}
              body={`1 ${payment?.exchangeRate?.base} = ${isPegged ? peggedRate : payment?.exchangeRate?.rate} ${payment?.exchangeRate?.counter}`}
              layout="horizontal"
              size="large"
            />

            <DataListItem
              id="processing-fee"
              testId="processing-fee"
              title={t('wallets.send.processingFee')}
              body={`${payment?.feeCurrency?.amount} ${payment?.feeCurrency?.currency}`}
              layout="horizontal"
              size="large"
            />

            <DataListItem
              id="network-fee"
              testId="network-fee"
              title={t('wallets.send.networkFee')}
              body={`${payment?.networkFeeCurrency?.amount} ${payment?.networkFeeCurrency?.currency}`}
              layout="horizontal"
              size="large"
            />
          </Box>

          <Box divideBox="top" className="border-stroke-static" paddingT={16} />

          <DataListItem
            id="total-subtracted"
            testId="total-subtracted"
            title={t('wallets.send.totalSubtracted')}
            body={`${payment?.totalWalletAmount} ${payment?.walletCurrency?.currency}`}
            layout="horizontal"
            size="large"
          />
        </Box>
      </Card>
      <Box flex justify="between" paddingT={16}>
        <Button testId="back" variant="secondary" onClick={walletAction?.onBack}>
          {t('back')}
        </Button>

        <Button
          testId="complete-payment"
          type="button"
          variant="primary"
          onClick={handleConfirmPayment}
          loading={isConfirmingPayment}
        >
          {t('wallets.send.completePayment')}
        </Button>
      </Box>
    </Container>
  )
}
const CountdownCallout = ({ onExpiry }: { onExpiry: () => void }) => {
  const { t } = useTranslation()
  const { formattedTime } = useMinutesSecondsTimer(5, 0, true, onExpiry)

  return (
    <Callout variant="warning">
      {t('contacts.paymentWillExpireInFiveMinutes', {
        minutes: `${formattedTime.minutes}:${formattedTime.seconds}`,
      })}
    </Callout>
  )
}
